import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PostCard from "../components/PostCard";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, ChatCircle, LockKey, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useMessageUser } from "../hooks/useMessageUser";
import { ProRequiredModal } from "./Messages";

function SingleComment({ c, user, onReplyClick, onDelete, onMessageAuthor, isReply, isLocked = false }) {
    const isSelf = user?.id === c.author_id;
    // Clickable comment-author name → starts a DM (Pro required).
    // Falls back to a plain span for the comment writer themselves.
    const NameEl = !isSelf && c.author_id ? (
        <button
            type="button"
            onClick={() => onMessageAuthor?.(c.author_id, c.author_name)}
            className="text-sm font-semibold hover:text-[#6C63FF] hover:underline underline-offset-2 transition-colors cursor-pointer text-left"
            data-testid={`comment-author-${c.id}`}
            aria-label={`Message ${c.author_name}`}
        >
            {c.author_name}
        </button>
    ) : (
        <div className="text-sm font-semibold">{c.author_name}</div>
    );
    return (
        <div className={isReply ? "ml-6 mt-3" : ""} data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-2 mb-1">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#6C63FF] to-[#FF8A00] grid place-items-center text-white text-xs font-bold">
                    {(c.author_name || "?").slice(0, 1).toUpperCase()}
                </div>
                {NameEl}
            </div>
            <p className="text-sm leading-relaxed text-foreground/90 ml-9">{c.content}</p>
            <div className="ml-9 mt-1 flex gap-3 text-xs">
                {!isReply && !isLocked && (
                    <button onClick={() => onReplyClick(c.id)} className="text-muted-foreground hover:text-[#6C63FF]" data-testid={`reply-toggle-${c.id}`}>
                        Reply
                    </button>
                )}
                {(user?.id === c.author_id || user?.role === "admin") && (
                    <button onClick={() => onDelete(c.id)} className="text-muted-foreground hover:text-red-500" data-testid={`delete-comment-${c.id}`}>
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
}

function ProPaywallCard({ post }) {
    return (
        <div
            className="my-8 rounded-2xl border-2 border-[#6C63FF]/30 bg-gradient-to-br from-[#6C63FF]/5 via-card to-[#FF8A00]/5 p-6 sm:p-8 text-center space-y-4"
            data-testid="pro-paywall-card"
        >
            <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#6C63FF] to-[#FF8A00] grid place-items-center text-white shadow-lg">
                <LockKey size={26} weight="fill" />
            </div>
            <div>
                <div className="font-display font-black text-xl sm:text-2xl tracking-tight">
                    Read the full idea with Pro
                </div>
                <div className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto leading-relaxed">
                    Ideas Mine Pro unlocks every category in
                    {post?.country ? <> <b className="text-foreground">{post.country}</b></> : null}, including <b className="text-foreground">{post?.category || "this category"}</b>, plus seller contact reveals on for-sale ideas.
                </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link
                    to="/subscription"
                    className="brand-button inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm shadow-md hover:shadow-lg transition-shadow"
                    data-testid="paywall-cta-pro"
                >
                    <Sparkle size={16} weight="fill" /> Get Ideas Mine Pro
                </Link>
                <Link
                    to="/auth?tab=signup"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm border border-border hover:bg-muted/50 transition-colors"
                    data-testid="paywall-cta-signup"
                >
                    Create free account
                </Link>
            </div>
            <div className="text-[11px] text-muted-foreground/70 pt-1">
                Free signup unlocks the Sample category & posting your own ideas.
            </div>
        </div>
    );
}

export default function PostDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [newC, setNewC] = useState("");
    const [loading, setLoading] = useState(true);
    const [replyTo, setReplyTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const { openMessageFor, proModalOpen, setProModalOpen } = useMessageUser();

    const handleMessageAuthor = (authorId, authorName) => {
        openMessageFor(authorId, { name: authorName });
    };

    const load = async () => {
        try {
            const p = await api.get(`/posts/${id}`);
            setPost(p.data);
            // Comments may 402 on Pro-locked posts. Silently treat as
            // empty list — the ProPaywallCard hides the discussion UI
            // anyway. Any other error just leaves comments empty.
            try {
                const c = await api.get(`/posts/${id}/comments`);
                setComments(c.data);
            } catch {
                setComments([]);
            }
        } catch {
            toast.error("Could not load post");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [id]);

    const submit = async () => {
        if (!user) {
            navigate(`/auth?next=${encodeURIComponent(window.location.pathname)}`);
            return;
        }
        if (!newC.trim()) return;
        const { data } = await api.post(`/posts/${id}/comments`, { content: newC });
        setComments([...comments, data]);
        setNewC("");
    };

    const submitReply = async () => {
        if (!replyText.trim() || !replyTo) return;
        const { data } = await api.post(`/posts/${id}/comments`, {
            content: replyText, parent_id: replyTo,
        });
        setComments([...comments, data]);
        setReplyText("");
        setReplyTo(null);
    };

    const delComment = async (cid) => {
        await api.delete(`/comments/${cid}`);
        setComments(comments.filter((x) => x.id !== cid));
    };

    if (loading) return <div className="p-10 text-center text-muted-foreground" data-testid="post-loading">Loading…</div>;
    if (!post) return <div className="p-10 text-center" data-testid="post-not-found">Post not found.</div>;

    const topLevel = comments.filter((c) => !c.parent_id);
    const repliesFor = (cid) => comments.filter((c) => c.parent_id === cid);
    const isLocked = !!post.is_locked;

    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 max-w-3xl mx-auto" data-testid="post-detail">
            <SEO
                title={post.title}
                description={post.description}
                path={`/post/${post.id}`}
                image={post.image_url || undefined}
                type="article"
                article={{
                    author: post.author_name,
                    datePublished: post.created_at,
                    section: post.category,
                    locked: isLocked,
                }}
            />
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-button">
                <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            <div className="paywalled-content">
                <PostCard post={post} onUpdate={setPost} detail />
            </div>

            {isLocked && <ProPaywallCard post={post} />}

            <section className="mt-10">
                <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
                    <ChatCircle size={20} weight="duotone" /> Discussion ({comments.length})
                </h2>

                {user ? (
                    isLocked ? (
                        <div
                            className="rounded-lg border border-[#6C63FF]/30 bg-[#6C63FF]/5 p-4 mb-6 text-sm"
                            data-testid="comment-locked-prompt"
                        >
                            <span className="mr-1">🔒</span>
                            <Link
                                to="/subscription"
                                className="text-[#6C63FF] font-semibold"
                                data-testid="comment-upgrade-cta"
                            >
                                Upgrade to Pro
                            </Link>
                            {" "}to join the discussion on this post.
                        </div>
                    ) : (
                        <div className="flex gap-2 mb-6">
                            <Textarea value={newC} onChange={(e) => setNewC(e.target.value)} placeholder="Share your thoughts…" data-testid="comment-input" />
                            <Button onClick={submit} className="brand-button shrink-0" data-testid="comment-submit">Comment</Button>
                        </div>
                    )
                ) : (
                    <div className="rounded-lg border border-border p-4 mb-6 text-sm text-muted-foreground" data-testid="comment-signin-prompt">
                        <Link to={`/auth?next=${encodeURIComponent(window.location.pathname)}`} className="text-[#6C63FF] font-semibold">Sign in</Link> to join the discussion.
                    </div>
                )}

                <div className="space-y-5">
                    {topLevel.length === 0 && (
                        <div className="text-sm text-muted-foreground">No comments yet — be the first.</div>
                    )}
                    {topLevel.map((c) => {
                        const replies = repliesFor(c.id);
                        return (
                            <div key={c.id} className="border-l-2 border-border pl-4">
                                <SingleComment c={c} user={user} onReplyClick={setReplyTo} onDelete={delComment} onMessageAuthor={handleMessageAuthor} isReply={false} isLocked={isLocked} />
                                {!isLocked && replyTo === c.id && (
                                    <div className="ml-9 mt-2 flex gap-2">
                                        <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply…" className="min-h-[60px]" data-testid={`reply-input-${c.id}`} />
                                        <Button onClick={submitReply} className="brand-button shrink-0" data-testid={`reply-submit-${c.id}`}>Reply</Button>
                                    </div>
                                )}
                                {replies.map((r) => (
                                    <SingleComment key={r.id} c={r} user={user} onReplyClick={() => {}} onDelete={delComment} onMessageAuthor={handleMessageAuthor} isReply={true} isLocked={isLocked} />
                                ))}
                            </div>
                        );
                    })}
                </div>
            </section>
            <ProRequiredModal
                open={proModalOpen}
                onOpenChange={setProModalOpen}
                action="message this user"
            />
        </div>
    );
}
